import { Box, Tooltip } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../components/CustomDataGridV2";
import Flex from "../../../../../../../components/Flex";
import { GlobalTableHeightWrapper } from "../../../../../../../components/GlobalTableHeightWrapper";
import StatusChip from "../../../../../../../components/StatusChip";
import Text from "../../../../../../../components/Text";
import { useGetPatientLedger } from "../../../../../../../queries/billings/ledger/use-get-patient-ledger";
import { NonNullableDate } from "../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
} from "../../../../../../../utils/utils";

const formatMoney = (v: any) => {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (Number.isNaN(n)) return "-";
  return `$ ${n.toFixed(2)}`;
};

const dashIfEmpty = (v: any) =>
  v === null || v === undefined || v === "" ? "-" : v;

const LedgerStatusVariantMap: Record<
  string,
  "success" | "error" | "warning" | "info" | "default" | "neutral" | "pending"
> = {
  PAID: "success",
  PARTIALLY_PAID: "warning",
  UNPAID: "error",
};

const getStatusLabel = (s?: string) => {
  if (!s) return "";
  return s
    .toString()
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const PatientLedger = () => {
  const patientId = useParams()?.patientId ?? "";
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);

  const {
    data: ledgerData,
    isLoading,
    isFetching,
  } = useGetPatientLedger({
    pagination,
    patientUuid: patientId,
    gridSorting,
  });

  const ledger: any = ledgerData;
  const rows: any[] =
    ledger?.data?.content ?? ledger?.content ?? ledger?.data ?? [];
  const totalElements =
    ledger?.data?.page?.totalElements ??
    ledger?.page?.totalElements ??
    rows?.length ??
    0;

  const COLUMNS = applyDefaultColumnSettings<any>(
    [
      {
        field: "claimId",
        flex: 0.35,
        minWidth: 80,
        headerName: "Claim ID",
        sortable: false,
        renderCell: ({ row }) => {
          const id = row?.claimId ?? row?.claimUuid ?? row?.uuid;
          return (
            <Text isTabletext>
              {id ? String(id).split("-")?.[0]?.toUpperCase() : "-"}
            </Text>
          );
        },
      },
      {
        field: "dateOfService",
        flex: 0.35,
        minWidth: 90,
        headerName: "DOS",
        renderCell: ({ row }) =>
          NonNullableDate(row?.dateOfService ?? row?.dos),
      },
      {
        field: "providerName",
        flex: 0.4,
        minWidth: 100,
        headerName: "Provider Name",
        sortable: false,
        renderCell: ({ row }) => (
          <Text isTabletext>{dashIfEmpty(row?.providerName)}</Text>
        ),
      },
      {
        field: "payerName",
        flex: 0.4,
        minWidth: 100,
        headerName: "Payer Name",
        sortable: false,
        renderCell: ({ row }) => (
          <Text isTabletext>
            {dashIfEmpty(row?.payerName ?? row?.insurancePayer?.payerName)}
          </Text>
        ),
      },
      {
        field: "totalCharge",
        flex: 0.3,
        minWidth: 90,
        headerName: "Total Charge",
        sortable: false,
        renderCell: ({ row }) => formatMoney(row?.totalCharge ?? row?.total),
      },
      {
        field: "insurancePayment",
        flex: 0.3,
        minWidth: 90,
        headerName: "Insurance Payment",
        sortable: false,
        renderCell: ({ row }) => formatMoney(row?.insurancePayment),
      },
      {
        field: "insuranceAdjustment",
        flex: 0.3,
        minWidth: 90,
        headerName: "Insurance Adjustment",
        sortable: false,
        renderCell: ({ row }) =>
          formatMoney(row?.insuranceAdjustment ?? row?.adjustment),
      },
      {
        field: "patientResponsibility",
        flex: 0.35,
        minWidth: 110,
        headerName: "Patient Responsibility",
        sortable: false,
        renderCell: ({ row }) => {
          const pr = row?.patientResponsibility;
          const copay = row?.copay;
          const coinsurance = row?.coinsurance;
          const deductible = row?.deductible;
          const breakdown = (
            <Box>
              <Text>Copay: {formatMoney(copay)}</Text>
              <Text>Coinsurance: {formatMoney(coinsurance)}</Text>
              <Text>Deductible: {formatMoney(deductible)}</Text>
            </Box>
          );
          return (
            <Tooltip title={breakdown} arrow placement="top">
              <Box sx={{ cursor: "pointer" }}>
                <Text isTabletext>{formatMoney(pr)}</Text>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "patientPaid",
        flex: 0.3,
        minWidth: 90,
        headerName: "Patient Paid",
        sortable: false,
        renderCell: ({ row }) => formatMoney(row?.patientPaid),
      },
      {
        field: "writeOff",
        flex: 0.3,
        minWidth: 90,
        headerName: "Write Off",
        sortable: false,
        renderCell: ({ row }) => formatMoney(row?.writeOff),
      },
      {
        field: "remainingBalance",
        flex: 0.3,
        minWidth: 90,
        headerName: "Remaining Balance",
        sortable: false,
        renderCell: ({ row }) => formatMoney(row?.remainingBalance),
      },
      {
        field: "status",
        flex: 0.3,
        minWidth: 100,
        headerName: "Status",
        sortable: false,
        renderCell: ({ row }) => {
          const status = (row?.status ?? "").toString().toUpperCase();
          if (!status) return <Text>-</Text>;
          return (
            <Flex>
              <StatusChip
                label={getStatusLabel(status)}
                variant={LedgerStatusVariantMap[status] ?? "default"}
              />
            </Flex>
          );
        },
      },
    ],
    ["dateOfService"]
  );

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <GlobalTableHeightWrapper marginTop={0}>
        <CustomDataGridV2
          getRowId={(row) =>
            row?.claimId ??
            row?.claimUuid ??
            row?.uuid ??
            `${row?.dateOfService}-${Math.random()}`
          }
          rows={rows}
          columns={COLUMNS}
          rowCount={totalElements}
          disableRowSelectionOnClick
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          onSortModelChange={setGridSorting}
          loading={isLoading || isFetching}
        />
      </GlobalTableHeightWrapper>
    </Box>
  );
};

export default PatientLedger;
